import { useState } from "react";
import { toast } from "sonner";
import { Reveal, Overline } from "../Reveal";
import api, { formatApiError } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";

export const Contact = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Votre message a bien été enregistré. Nous revenons vers vous très vite.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-white border border-linen rounded-2xl px-5 py-4 text-sm text-ink placeholder:text-smoke/60 focus:outline-none focus:ring-2 focus:ring-sage/50 transition-shadow duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-28 md:py-40 bg-bone">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 md:gap-20">
        <Reveal>
          <Overline>{t("contact.overline")}</Overline>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl tracking-tight text-ink leading-tight">
            {t("contact.title")}
          </h2>
          <p className="mt-8 text-base md:text-lg leading-relaxed text-smoke max-w-md">
            {t("contact.sub")}
          </p>
          <div className="mt-10 space-y-2 text-sm text-smoke">
            <p data-testid="contact-address" className="text-ink">{t("footer.address")}</p>
            <p data-testid="contact-phone">{t("footer.phone")}</p>
          </div>
          <p className="mt-10 font-serif italic text-xl md:text-2xl text-sagedark">
            « Une période de transition est un moteur puissant pour choisir une trajectoire qui nous ressemble. »
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="bg-white rounded-3xl p-8 md:p-10 flex flex-col gap-5">
            <div>
              <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("contact.name")}</label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                className={`${field} mt-2`}
                placeholder={t("contact.namePh")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("contact.email")}</label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                className={`${field} mt-2`}
                placeholder={t("contact.emailPh")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("contact.message")}</label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                rows={5}
                className={`${field} mt-2 resize-none`}
                placeholder={t("contact.messagePh")}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="mt-2 rounded-full bg-ink text-paper px-8 py-4 text-sm tracking-wide hover:bg-sagedark disabled:opacity-50 transition-colors duration-300"
            >
              {sending ? t("contact.sending") : t("contact.submit")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
